import { Dialog, Transition } from "@headlessui/react";
import { useMutation } from "@tanstack/react-query";
import { Fragment } from "react";
import { TriangleAlert, X } from "lucide-react";
import { queryClient } from "../../main";

interface DeleteModalProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  mutationFn: (id: string) => Promise<void>;
  itemId: string;
  queryKey?: string;
  itemName?: string;
  message?: string;
}

const DeleteModal: React.FC<DeleteModalProps> = ({
  isOpen,
  setIsOpen,
  mutationFn,
  itemId,
  queryKey,
  itemName,
  message,
}) => {
  const { mutate: deleteItem, isPending } = useMutation({
    mutationFn: () => mutationFn(itemId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKey] });
      setIsOpen(false);
    },
  });

  return (
    <Transition appear show={isOpen}>
      <Dialog
        as="div"
        className="relative z-[999999999999999] focus:outline-none"
        onClose={() => !isPending && setIsOpen(false)}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-primary/55 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 z-[999999999999999] w-screen overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="relative w-full max-w-md rounded-2xl border border-black/10 bg-white p-6 shadow-[0_30px_90px_rgba(17,24,22,0.26)]">
                <button
                  onClick={() => setIsOpen(false)}
                  disabled={isPending}
                  aria-label="Close"
                  className="absolute right-4 top-4 rounded-lg p-1 text-lightGray transition-colors hover:bg-black/5 hover:text-textPrimary disabled:opacity-40"
                >
                  <X size={20} />
                </button>

                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-red-50 text-red-600">
                  <TriangleAlert size={24} />
                </div>

                <Dialog.Title className="mt-4 text-center text-lg font-bold text-textPrimary">
                  Delete this item?
                </Dialog.Title>

                {itemName && (
                  <p className="mt-1 break-words text-center text-sm font-semibold text-textPrimary">
                    "{itemName}"
                  </p>
                )}

                <p className="mt-2 text-center text-sm text-lightGray">
                  {message ?? "This action cannot be undone."}
                </p>

                <div className="mt-6 flex gap-3">
                  <button
                    onClick={() => setIsOpen(false)}
                    disabled={isPending}
                    className="flex-1 rounded-xl border border-black/10 py-2.5 text-sm font-semibold text-textPrimary transition-colors hover:bg-dashboardBg disabled:opacity-40"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => deleteItem()}
                    disabled={isPending}
                    className="flex-1 rounded-xl bg-red-600 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-red-800 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {isPending ? "Deleting..." : "Delete"}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default DeleteModal;
